import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { CenterRect } from "../../core/managers/CenterCache";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";
import { PhysicsBody } from "../../core/physics/PhysicsBody";
import { SpringForce } from "../../core/physics/forces/SpringForce";

interface ImpulseBodies {
  posX: PhysicsBody;
  posY: PhysicsBody;
  rot: PhysicsBody;
  springX: SpringForce;
  springY: SpringForce;
  springRot: SpringForce;
}

export class StringImpulse extends StringModule {
  private originObservers = new WeakMap<StringObject, MutationObserver>();
  private bodies = new WeakMap<StringObject, ImpulseBodies>();

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "impulse";
    this.cssProperties = [
      { name: "--push-x", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--push-y", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--push-rotation", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.attributesToMap.push(
      { key: "position-strength", type: "number", fallback: this.settings["position-strength"] },
      { key: "position-tension", type: "number", fallback: this.settings["position-tension"] },
      { key: "position-friction", type: "number", fallback: this.settings["position-friction"] },
      {
        key: "position-max-velocity",
        type: "number",
        fallback: this.settings["position-max-velocity"],
      },
      {
        key: "position-update-threshold",
        type: "number",
        fallback: this.settings["position-update-threshold"],
      },

      // rotate
      { key: "rotation-strength", type: "number", fallback: this.settings["rotation-strength"] },
      { key: "rotation-tension", type: "number", fallback: this.settings["rotation-tension"] },
      { key: "rotation-friction", type: "number", fallback: this.settings["rotation-friction"] },
      {
        key: "rotation-max-angular-velocity",
        type: "number",
        fallback: this.settings["rotation-max-angular-velocity"],
      },
      { key: "rotation-max-angle", type: "number", fallback: this.settings["rotation-max-angle"] },
      {
        key: "rotation-update-threshold",
        type: "number",
        fallback: this.settings["rotation-update-threshold"],
      },

      { key: "max-offset", type: "number", fallback: this.settings["max-offset"] },
      { key: "sleep-epsilon", type: "number", fallback: this.settings["sleep-epsilon"] },
      { key: "continuous-push", type: "boolean", fallback: this.settings["continuous-push"] },
      {
        key: "rotation-origin",
        type: "string",
        fallback: this.settings["rotation-origin"] ?? "center center",
      }
    );
  }

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);

    const springX = new SpringForce(0, 0);
    const springY = new SpringForce(0, 0);
    const springRot = new SpringForce(0, 0);

    const posX = new PhysicsBody();
    posX.addForce(springX);
    const posY = new PhysicsBody();
    posY.addForce(springY);
    const rot = new PhysicsBody({ restitution: 0.35 });
    rot.addForce(springRot);

    this.bodies.set(object, { posX, posY, rot, springX, springY, springRot });

    object.setProperty("__prev-css-x", 0);
    object.setProperty("__prev-css-y", 0);
    object.setProperty("__prev-css-rot", 0);

    object.setProperty("__push-latch", false);
    object.setProperty("__rotate-latch", false);

    // Cache parsed rotation origin
    this.cacheRotationOrigin(object);

    // Watch for rotation-origin attribute changes
    this.observeRotationOrigin(object);

    this.hover.track(object);
    this.centers.attach(object);
  }

  override onObjectDisconnected(object: StringObject): void {
    this.hover.untrack(object);
    this.centers.detach(object);
    this.bodies.delete(object);

    // Cleanup rotation-origin observer
    const observer = this.originObservers.get(object);
    if (observer) {
      observer.disconnect();
      this.originObservers.delete(object);
    }
  }

  override onMouseMoveMeasure(): void {
    const cursorVelocityX = this.data.cursor.velocityX;
    const cursorVelocityY = this.data.cursor.velocityY;
    if (cursorVelocityX === 0 && cursorVelocityY === 0) return;

    const mouseX = this.data.cursor.targetX;
    const mouseY = this.data.cursor.targetY;

    for (const object of this.objects) {
      const rect = this.centers.getRect(object);
      if (!rect) continue;
      const isUnderCursor =
        mouseX >= rect.left && mouseX <= rect.right && mouseY >= rect.top && mouseY <= rect.bottom;

      if (isUnderCursor) {
        const bodies = this.bodies.get(object);
        if (!bodies) continue;

        {
          const w = rect.width || 1;
          const side01 = Math.max(0, Math.min(1, (this.data.cursor.targetX - rect.left) / w));
          this.events.emit(this.getObjectEventName(object, "object:impulse", "side"), {
            value: side01
          });
        }

        const positionStrength = object.getProperty<number>("position-strength") || 0;
        if (positionStrength !== 0) {
          const isContinuousPush = object.getProperty<boolean>("continuous-push") ?? true;
          const isLatched = object.getProperty<boolean>("__push-latch") === true;
          if (isContinuousPush || !isLatched) {
            bodies.posX.applyImpulse(cursorVelocityX * positionStrength);
            bodies.posY.applyImpulse(cursorVelocityY * positionStrength);
            if (!isContinuousPush) object.setProperty("__push-latch", true);
            this.wake(object);
          }
        }

        const rotationStrength = object.getProperty<number>("rotation-strength") ?? 0.75;
        if (rotationStrength !== 0) {
          const isContinuousPush = object.getProperty<boolean>("continuous-push") ?? true;
          const isLatched = object.getProperty<boolean>("__rotate-latch") === true;

          if (isContinuousPush || !isLatched) {
            const { centerX, centerY } = this.getRotationOriginFromRect(object, rect);
            const relativeX = mouseX - centerX;
            const relativeY = mouseY - centerY;
            const torque = relativeX * cursorVelocityY - relativeY * cursorVelocityX;

            bodies.rot.applyImpulse(torque * rotationStrength * 0.02);
            if (!isContinuousPush) object.setProperty("__rotate-latch", true);
            this.wake(object);
          }
        }
      }
    }
  }

  /**
   * Parses and caches the rotation origin for an object.
   * Called once on object connection and when attribute changes.
   */
  private cacheRotationOrigin(object: StringObject): void {
    const originValue = object.getProperty<string>("rotation-origin") ?? "center center";
    const { x, y } = this.tools.originParser.toNormalized({ value: originValue });
    object.setProperty("__rotation-origin-x", x);
    object.setProperty("__rotation-origin-y", y);
  }

  /**
   * Observes changes to the rotation-origin attribute and re-caches when changed.
   */
  private observeRotationOrigin(object: StringObject): void {
    const attrName = "string-rotation-origin";
    const dataAttrName = "data-string-rotation-origin";

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          (mutation.attributeName === attrName || mutation.attributeName === dataAttrName)
        ) {
          // Re-read attribute and update cache
          const newValue =
            object.htmlElement.getAttribute(attrName) ??
            object.htmlElement.getAttribute(dataAttrName);
          if (newValue !== null) {
            object.setProperty("rotation-origin", newValue);
          }
          this.cacheRotationOrigin(object);
          break;
        }
      }
    });

    observer.observe(object.htmlElement, {
      attributes: true,
      attributeFilter: [attrName, dataAttrName],
    });

    this.originObservers.set(object, observer);
  }

  /**
   * Calculates the rotation origin point using cached normalized values and provided rect.
   * Avoids re-parsing origin string and extra getBoundingClientRect calls.
   */
  private getRotationOriginFromRect(
    object: StringObject,
    rect: CenterRect
  ): { centerX: number; centerY: number } {
    const normalizedX = object.getProperty<number>("__rotation-origin-x") ?? 0.5;
    const normalizedY = object.getProperty<number>("__rotation-origin-y") ?? 0.5;

    return {
      centerX: rect.left + rect.width * normalizedX,
      centerY: rect.top + rect.height * normalizedY,
    };
  }

  onFrame(_: StringData): void {
    const mouseX = this.data.cursor.targetX;
    const mouseY = this.data.cursor.targetY;

    for (const objectItem of this.activeObjects) {
      const rect = this.centers.getRect(objectItem);
      const isUnderCursor =
        rect != null &&
        mouseX >= rect.left &&
        mouseX <= rect.right &&
        mouseY >= rect.top &&
        mouseY <= rect.bottom;

      if (!isUnderCursor && objectItem.getProperty<boolean>("__push-latch") === true) {
        objectItem.setProperty("__push-latch", false);
      }
      if (!isUnderCursor && objectItem.getProperty<boolean>("__rotate-latch") === true) {
        objectItem.setProperty("__rotate-latch", false);
      }
    }

    for (const object of this.activeObjects) {
      const bodies = this.bodies.get(object);
      if (!bodies) continue;

      const positionTension = object.getProperty<number>("position-tension") ?? 0.05;
      const positionFriction = object.getProperty<number>("position-friction") ?? 0.15;
      const maxVelocity = object.getProperty<number>("position-max-velocity") ?? 120;
      const maxOffset = object.getProperty<number>("max-offset") ?? 220;

      bodies.springX.tension = positionTension;
      bodies.springY.tension = positionTension;
      bodies.posX.friction = positionFriction;
      bodies.posY.friction = positionFriction;
      bodies.posX.maxVelocity = maxVelocity;
      bodies.posY.maxVelocity = maxVelocity;
      bodies.posX.maxValue = maxOffset;
      bodies.posY.maxValue = maxOffset;

      bodies.posX.step();
      bodies.posY.step();

      const rotationTension = object.getProperty<number>("rotation-tension") ?? 0.06;
      const rotationFriction = object.getProperty<number>("rotation-friction") ?? 0.18;
      const maxAngularVelocityDeg =
        object.getProperty<number>("rotation-max-angular-velocity") ?? 6;
      const maxRotationAngleDeg = object.getProperty<number>("rotation-max-angle") ?? 18;

      bodies.springRot.tension = rotationTension;
      bodies.rot.friction = rotationFriction;
      bodies.rot.maxVelocity = maxAngularVelocityDeg;
      bodies.rot.maxValue = maxRotationAngleDeg;

      bodies.rot.step();

      const sleepEpsilon = object.getProperty<number>("sleep-epsilon") ?? 0.01;
      const isPositionAsleep =
        bodies.posX.velocity * bodies.posX.velocity + bodies.posY.velocity * bodies.posY.velocity <
          sleepEpsilon * sleepEpsilon &&
        bodies.posX.value * bodies.posX.value + bodies.posY.value * bodies.posY.value <
          sleepEpsilon * sleepEpsilon;

      const isRotationAsleep = bodies.rot.isAtRest(sleepEpsilon);

      if (isPositionAsleep) {
        bodies.posX.reset();
        bodies.posY.reset();
      }

      if (isRotationAsleep) {
        bodies.rot.reset();
      }

      const positionUpdateThreshold =
        object.getProperty<number>("position-update-threshold") ?? 0.1;
      const rotationUpdateThreshold =
        object.getProperty<number>("rotation-update-threshold") ?? 0.15;

      const prevCssX = object.getProperty<number>("__prev-css-x") || 0;
      const prevCssY = object.getProperty<number>("__prev-css-y") || 0;
      const prevCssRot = object.getProperty<number>("__prev-css-rot") || 0;

      const cssTranslateX = Math.round(bodies.posX.value * 10) / 10;
      const cssTranslateY = Math.round(bodies.posY.value * 10) / 10;
      const cssRotateDeg = Math.round(bodies.rot.value * 10) / 10;

      const needsPositionUpdate =
        Math.abs(cssTranslateX - prevCssX) > positionUpdateThreshold ||
        Math.abs(cssTranslateY - prevCssY) > positionUpdateThreshold;

      const needsRotationUpdate = Math.abs(cssRotateDeg - prevCssRot) > rotationUpdateThreshold;

      if (needsPositionUpdate || needsRotationUpdate) {
        object.setProperty("__next-css-x", cssTranslateX);
        object.setProperty("__next-css-y", cssTranslateY);
        object.setProperty("__next-css-rot", cssRotateDeg);
      }

      object.setProperty("__needs-position-update", needsPositionUpdate);
      object.setProperty("__needs-rotation-update", needsRotationUpdate);

      const pushLatch = object.getProperty<boolean>("__push-latch") === true;
      const rotateLatch = object.getProperty<boolean>("__rotate-latch") === true;
      if (isPositionAsleep && isRotationAsleep && !pushLatch && !rotateLatch) {
        this.sleep(object);
      }
    }
  }

  override onMutate(): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      const needsPositionUpdate = object.getProperty<boolean>("__needs-position-update") === true;
      const needsRotationUpdate = object.getProperty<boolean>("__needs-rotation-update") === true;

      if (!needsPositionUpdate && !needsRotationUpdate) {
        continue;
      }

      const cssTranslateX = object.getProperty<number>("__next-css-x") || 0;
      const cssTranslateY = object.getProperty<number>("__next-css-y") || 0;
      const cssRotateDeg = object.getProperty<number>("__next-css-rot") || 0;

      this.applyToElementAndConnects(object, (el) => {
        if (needsPositionUpdate) {
          styleTxn.setVar(el, "--push-x", cssTranslateX);
          styleTxn.setVar(el, "--push-y", cssTranslateY);
        }
        if (needsRotationUpdate) {
          styleTxn.setVar(el, "--push-rotation", cssRotateDeg);
        }
      });

      if (needsPositionUpdate) {
        object.setProperty("__prev-css-x", cssTranslateX);
        object.setProperty("__prev-css-y", cssTranslateY);
        this.events.emit(this.getObjectEventName(object, "object:impulse", "move"), {
          x: cssTranslateX,
          y: cssTranslateY,
        });
      }
      if (needsRotationUpdate) {
        object.setProperty("__prev-css-rot", cssRotateDeg);
        this.events.emit(this.getObjectEventName(object, "object:impulse", "rotate"), {
          rotation: cssRotateDeg
        });
      }

      this.centers.invalidate(object);

      object.setProperty("__needs-position-update", false);
      object.setProperty("__needs-rotation-update", false);
    }
  }
}
